"use client";

import * as React from "react";
import { Check } from "lucide-react";

import { MarketingTrackedLink } from "@/components/site/primitives/marketing-tracked-link";
import { SectionHeader } from "@/components/site/primitives/section-header";
import { PricingSignupLink } from "@/components/site/sections/pricing-signup-link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  getDiscountPercent,
  maxPlanDiscountPercent,
  paidPlans,
  rolloutPlan,
} from "@/lib/content/pricing";
import { homeSectionAnchors } from "@/lib/content/navigation";
import { cn } from "@/lib/utils";

type BillingCycle = "monthly" | "yearly";

export function PricingSection() {
  const [billing, setBilling] = React.useState<BillingCycle>("yearly");

  return (
    <section id={homeSectionAnchors.pricing} className="site-section">
      <div className="site-shell site-stack">
        <SectionHeader align="center" title="Pricing" />

        <FlatPricePromise />

        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-muted/20 p-1">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              className={cn(
                "min-w-[7.5rem] rounded-full px-4 py-2 text-sm font-medium",
                billing === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={billing === "monthly"}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling("yearly")}
              className={cn(
                "inline-flex min-w-[12.25rem] items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium",
                billing === "yearly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
              aria-pressed={billing === "yearly"}
            >
              <span>Yearly</span>
              <span className="rounded-full border border-emerald-300/80 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                Save up to {maxPlanDiscountPercent}%
              </span>
            </button>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="h-full border-foreground/10 bg-[linear-gradient(180deg,rgba(17,17,17,0.045)_0%,rgba(17,17,17,0.015)_100%)] p-6 md:p-7">
            <div className="flex h-full flex-col">
              <div className="grid gap-y-4 lg:h-[20rem] lg:grid-rows-[2rem_4.25rem_1.5rem_minmax(0,1fr)]">
                <div className="flex items-start">
                  <div className="inline-flex w-fit rounded-full border border-transparent bg-foreground px-3 py-1 text-xs font-medium text-background">
                    {rolloutPlan.name}
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <div className="text-4xl font-semibold tracking-[-0.03em] text-foreground">
                    {rolloutPlan.price}
                  </div>
                  <div className="pb-1 text-sm text-muted-foreground">
                    {rolloutPlan.duration}
                  </div>
                </div>
                <div aria-hidden="true" />
                <p className="site-body-strong">
                  {rolloutPlan.description}
                  <strong className="mt-2 block font-semibold text-foreground">
                    {rolloutPlan.emphasized}
                  </strong>
                </p>
              </div>

              <div className="my-5 border-t border-border/60" />

              <div className="space-y-3">
                {rolloutPlan.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-3">
                    <Check className="mt-1 size-4 shrink-0 text-foreground" />
                    <span className="text-sm leading-6 text-foreground">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3">
                <Button asChild>
                  <MarketingTrackedLink
                    href={rolloutPlan.primaryCta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    eventName="guided_rollout_clicked"
                    eventProperties={{
                      app_area: "pricing",
                      cta_id: "pricing_guided_rollout_primary",
                      placement: "pricing_card",
                      route_name: "home.pricing",
                    }}
                  >
                    {rolloutPlan.primaryCta.label}
                  </MarketingTrackedLink>
                </Button>
                <Button asChild variant="outline">
                  <MarketingTrackedLink
                    href={rolloutPlan.secondaryCta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    eventName="demo_cta_clicked"
                    eventProperties={{
                      app_area: "pricing",
                      cta_id: "pricing_guided_rollout_demo",
                      placement: "pricing_card",
                      route_name: "home.pricing",
                    }}
                  >
                    {rolloutPlan.secondaryCta.label}
                  </MarketingTrackedLink>
                </Button>
              </div>
            </div>
          </Card>

          {paidPlans.map((plan) => {
            const isYearly = billing === "yearly";
            const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
            const savings = getDiscountPercent(plan.monthlyPrice, plan.yearlyPrice);

            return (
              <Card
                key={plan.name}
                className="h-full border-border/60 bg-background p-6 md:p-7"
              >
                <div className="flex h-full flex-col">
                  <div className="grid gap-y-4 lg:h-[20rem] lg:grid-rows-[2rem_4.25rem_1.5rem_minmax(0,1fr)]">
                    <div className="flex items-start">
                      <div className="inline-flex w-fit rounded-full border border-border/70 px-3 py-1 text-xs font-medium text-muted-foreground">
                        {plan.name}
                      </div>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="min-w-[3.25rem] text-4xl font-semibold tracking-[-0.03em] tabular-nums text-foreground">
                        £{price}
                      </div>
                      <div className="pb-1 text-sm text-muted-foreground">
                        {plan.priceSuffix}
                      </div>
                    </div>
                    <div className="flex min-h-6 items-center gap-2 text-xs text-muted-foreground">
                      <span>{isYearly ? plan.yearlyNote : plan.monthlyNote}</span>
                      <span
                        className={cn(
                          "rounded-full border border-emerald-300/80 bg-emerald-50 px-2 py-0.5 font-semibold tabular-nums text-emerald-700 transition-opacity",
                          !isYearly && "invisible",
                        )}
                      >
                        Save {savings}%
                      </span>
                    </div>
                    <p className="site-body">{plan.description}</p>
                  </div>

                  <div className="my-5 border-t border-border/60" />

                  <div className="space-y-3">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <Check className="mt-1 size-4 shrink-0 text-foreground" />
                        <span className="text-sm leading-6 text-foreground">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 space-y-3">
                    <Button asChild className="w-full">
                      <PricingSignupLink
                        billingCycle={billing}
                        planCode={plan.planCode}
                      >
                        Start 30-day trial
                      </PricingSignupLink>
                    </Button>
                    <p className="text-center text-xs leading-5 text-muted-foreground">
                      No credit card required. Full access for 30 days.
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className="border-border/60 bg-[linear-gradient(180deg,rgba(17,17,17,0.02)_0%,rgba(17,17,17,0.01)_100%)] p-5 md:p-6">
          <div className="grid gap-6 md:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] md:items-end md:gap-8">
            <div className="max-w-xl space-y-4 md:order-2">
              <div className="inline-flex w-fit rounded-full border border-border/70 px-3 py-1 text-xs font-medium text-muted-foreground">
                For Enterprises
              </div>

              <div className="space-y-2">
                <h3 className="text-balance text-2xl font-semibold tracking-[-0.03em] text-foreground md:text-[2rem]">
                  Need more teams or team members?
                </h3>
                <p className="site-body max-w-xl">
                  If you need a broader rollout, more seats, or a setup that
                  does not fit neatly into the standard plans, we are happy to
                  shape a custom arrangement with you.
                </p>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild>
                  <MarketingTrackedLink
                    href="mailto:support@cooperly.ai?subject=Enterprise%20pricing%20inquiry"
                    eventName="contact_cta_clicked"
                    eventProperties={{
                      app_area: "pricing",
                      cta_id: "pricing_enterprise_contact",
                      placement: "enterprise_contact",
                      route_name: "home.pricing",
                    }}
                  >
                    Let&apos;s Talk
                  </MarketingTrackedLink>
                </Button>
                <Button asChild variant="outline">
                  <MarketingTrackedLink
                    href="https://cal.eu/cooperly/demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    eventName="demo_cta_clicked"
                    eventProperties={{
                      app_area: "pricing",
                      cta_id: "pricing_enterprise_demo",
                      placement: "enterprise_demo",
                      route_name: "home.pricing",
                    }}
                  >
                    Book a demo
                  </MarketingTrackedLink>
                </Button>
              </div>
            </div>

            <EnterpriseScaleVisual className="md:order-1" />
          </div>
        </Card>
      </div>
    </section>
  );
}

function FlatPricePromise() {
  return (
    <div className="rounded-[1.1rem] border border-foreground bg-foreground px-5 py-5 text-background shadow-[0_18px_60px_-44px_rgba(15,23,42,0.55)] md:px-7 md:py-6">
      <div className="grid gap-3 md:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] md:items-center md:gap-8">
        <div>
          <h3 className="text-balance text-2xl font-semibold leading-[1.04] tracking-[-0.04em] text-background md:text-[2.15rem]">
            Your whole team, one flat price.
          </h3>
        </div>

        <div className="border-t border-background/14 pt-3 md:border-l md:border-t-0 md:pl-8 md:pt-0">
          <p className="max-w-2xl text-pretty text-sm leading-6 text-background/72 md:text-[0.98rem] md:leading-7">
            Choose the operating scope you need: one team with up to 10 people,
            or up to five teams and 50 people. Your price stays flat within the
            published limit of the plan.
          </p>
        </div>
      </div>
    </div>
  );
}

function EnterpriseScaleVisual({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border/60 bg-muted/20 p-3.5 md:p-4",
        className,
      )}
    >
      <div className="grid min-h-[11.25rem] grid-cols-[minmax(0,0.84fr)_minmax(0,1.16fr)] gap-3">
        <div className="relative rounded-2xl border border-border/60 bg-background p-3.5 shadow-[0_18px_35px_-28px_rgba(15,23,42,0.16)]">
          <div className="inline-flex rounded-full border border-border/70 px-2.5 py-1 text-[10px] font-medium text-muted-foreground">
            Enterprise setup
          </div>
          <div className="mt-3 space-y-1.5">
            <GhostLine width="54%" />
            <GhostLine width="78%" />
            <GhostLine width="62%" />
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            <ScaleChip>Custom seats</ScaleChip>
            <ScaleChip>Multi-team</ScaleChip>
            <ScaleChip>Shared rollout</ScaleChip>
          </div>
          <div className="pointer-events-none absolute right-3 top-3 h-16 w-16 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.9),transparent_68%)]" />
        </div>

        <div className="relative grid gap-3">
          <div className="absolute left-[-0.95rem] top-[2.2rem] hidden h-px w-5 bg-border/70 md:block" />
          <div className="absolute left-[-0.95rem] top-[4.55rem] hidden h-px w-5 bg-border/70 md:block" />
          <div className="absolute left-[-0.95rem] bottom-[2.7rem] hidden h-px w-5 bg-border/70 md:block" />

          <ScaleCard
            label="Team clusters"
            widths={["66%", "82%", "58%"]}
            chip="5+ workspaces"
          />
          <ScaleCard
            label="Access layers"
            widths={["52%", "74%", "64%"]}
            chip="Custom roles"
          />
          <ScaleCard
            label="Review cadence"
            widths={["61%", "71%", "49%"]}
            chip="Flexible support"
          />
        </div>
      </div>
    </div>
  );
}

function GhostLine({ width }: { width: string }) {
  return <div className="h-1.5 rounded-full bg-foreground/10" style={{ width }} />;
}

function ScaleChip({ children }: React.PropsWithChildren) {
  return (
    <span className="inline-flex shrink-0 whitespace-nowrap rounded-full border border-border/70 bg-background px-2.5 py-1 text-[10px] font-medium leading-none text-muted-foreground shadow-sm">
      {children}
    </span>
  );
}

function ScaleCard({
  label,
  widths,
  chip,
}: {
  label: string;
  widths: [string, string, string];
  chip: string;
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-background px-3 py-2.5 shadow-[0_16px_30px_-28px_rgba(15,23,42,0.14)]">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-medium text-foreground">{label}</div>
        <ScaleChip>{chip}</ScaleChip>
      </div>
      <div className="mt-2.5 space-y-1.5">
        <GhostLine width={widths[0]} />
        <GhostLine width={widths[1]} />
        <GhostLine width={widths[2]} />
      </div>
    </div>
  );
}
